/**
 * Raid bark emitters — messageKey only (no t() / Phaser).
 * Kept separate from raid-rules so assault / brute-skills can emit
 * without circular imports.
 */

import { RaidEvent } from "./raid-events";
import type { RaidBattleState, RaidUnit } from "./raid-types";
import {
  RaidEnemyDefId,
  RaidEnemyTag,
  enemyHasTag,
} from "./raid-enemy";
import {
  addCombatBarkChance,
  addCombatBarkCooldownSec,
  assaultChunkBarkChance,
  assaultChunkBarkCooldownSec,
  assaultChunkBarkMinCount,
  bruteBarkChance,
  bruteBarkCooldownSec,
  bruteBarkSpawnChance,
  pickAddCombatBarkKey,
  pickAddWaveBarkKey,
  pickAddWaveSpeaker,
  pickBruteBarkKey,
  rollBarkGate,
  type RaidBruteBarkMoment,
} from "./raid-bark";
import { raidCosmeticRandom } from "./raid-rng";

function livingThralls(state: RaidBattleState): RaidUnit[] {
  return state.units.filter(
    (u) =>
      u.team === 1 &&
      u.hp > 0 &&
      enemyHasTag(u, RaidEnemyTag.PackChew),
  );
}

/**
 * Occasional thrall bark on assault drip chunk spawn.
 * Uses shared encounter.addBarkCdLeft (not spammy across the stream).
 */
export function tryEmitAssaultChunkBark(
  state: RaidBattleState,
  spawned: readonly RaidUnit[],
  roll?: number,
): boolean {
  const r = roll ?? raidCosmeticRandom(state);
  if (spawned.length < assaultChunkBarkMinCount()) return false;
  if (
    !rollBarkGate(
      assaultChunkBarkChance(),
      state.encounter.addBarkCdLeft,
      r,
    )
  ) {
    return false;
  }
  const speaker = pickAddWaveSpeaker(
    spawned,
    Math.floor(state.time * 1000) + spawned.length,
  );
  if (!speaker) return false;
  state.events.push({
    kind: RaidEvent.Bark,
    unitId: speaker.id,
    x: speaker.x,
    y: speaker.y,
    messageKey: pickAddWaveBarkKey(speaker.id),
  });
  state.encounter.addBarkCdLeft = assaultChunkBarkCooldownSec();
  return true;
}

/**
 * Occasional mid-wave thrall combat bark (one speaker among living adds).
 * Assault mode only; shares encounter.addBarkCdLeft with drip chunk bark.
 */
export function tryEmitAddCombatBark(
  state: RaidBattleState,
  roll?: number,
): boolean {
  const r = roll ?? raidCosmeticRandom(state);
  if (state.encounter.mode !== "assault") return false;
  if (state.phase !== "playing") return false;
  const thralls = livingThralls(state);
  if (thralls.length === 0) return false;
  if (
    !rollBarkGate(
      addCombatBarkChance(),
      state.encounter.addBarkCdLeft,
      r,
    )
  ) {
    return false;
  }
  const speaker = pickAddWaveSpeaker(
    thralls,
    Math.floor(state.time * 1000) + thralls.length,
  );
  if (!speaker) return false;
  state.events.push({
    kind: RaidEvent.Bark,
    unitId: speaker.id,
    x: speaker.x,
    y: speaker.y,
    messageKey: pickAddCombatBarkKey(speaker.id + Math.floor(state.time)),
  });
  state.encounter.addBarkCdLeft = addCombatBarkCooldownSec();
  return true;
}

/**
 * Elite brute bark — spawn / brass telegraph / leap / break.
 * Per-brute barkCdLeft; spawn uses higher chance.
 */
export function tryEmitBruteBark(
  state: RaidBattleState,
  brute: RaidUnit,
  moment: RaidBruteBarkMoment,
  roll?: number,
): boolean {
  const r = roll ?? raidCosmeticRandom(state);
  if (brute.defId !== "brute" || brute.hp <= 0) return false;
  const chance =
    moment === "spawn" ? bruteBarkSpawnChance() : bruteBarkChance();
  if (!rollBarkGate(chance, brute.barkCdLeft, r)) return false;
  const messageKey = pickBruteBarkKey(
    moment,
    brute.id + Math.floor(state.time * 100) + moment.length,
  );
  state.events.push({
    kind: RaidEvent.Bark,
    unitId: brute.id,
    x: brute.x,
    y: brute.y,
    messageKey,
  });
  brute.barkCdLeft = bruteBarkCooldownSec();
  return true;
}

/** One bark from a newly spawned brute pack (prefer first armed slot). */
export function tryEmitBrutePackSpawnBark(
  state: RaidBattleState,
  spawned: readonly RaidUnit[],
  roll?: number,
): boolean {
  const r = roll ?? raidCosmeticRandom(state);
  const brutes = spawned.filter(
    (u) => u.defId === RaidEnemyDefId.Brute && u.hp > 0,
  );
  if (brutes.length === 0) return false;
  const speaker = pickAddWaveSpeaker(
    brutes,
    Math.floor(state.time * 1000) + brutes.length,
  );
  if (!speaker) return false;
  return tryEmitBruteBark(state, speaker, "spawn", r);
}
